import React, { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Textarea } from './ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Slider } from './ui/slider'
import { Checkbox } from './ui/checkbox'
import { Badge } from './ui/badge'
import { Label } from './ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible'
import { CheckCircleIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon, BuildingOfficeIcon, UserIcon, CalculatorIcon, DocumentIcon } from '@heroicons/react/24/outline'
import { Sparkles, Loader2, RotateCcw, Package, Search, X, ChevronRight, Edit2, Trash2, GitBranch, Printer, Video, Link as LinkIcon, Copy, Mail, Target } from 'lucide-react'
import { toast } from 'react-hot-toast'

// Import original components
import TopNavigation from './TopNavigation'
import QuoteManager from './QuoteManager'
import CustomerInfo from './CustomerInfo'
import CustomerManager from './CustomerManager'
import ServiceManagement from './ServiceManagement'
import PackageManager from './PackageManager'
import RevenueManager from './RevenueManager'
import ExpenseManager from './ExpenseManager'
import CashflowDashboard from './CashflowDashboard'
import Roadmap from './Roadmap'
import DataExportImport from './DataExportImport'
import TemplateManager from './TemplateManager'
import CompanyManager from './CompanyManager'
import PdfPreview from './PdfPreview'
import ServiceTooltip from './ServiceTooltip'
import QuoteSectionEditor from './QuoteSectionEditor'
import EnhancedQuoteSectionEditor from './EnhancedQuoteSectionEditor'
import ServiceAlternativesDialog from './ServiceAlternativesDialog'
import ServiceAlternativesBadge from './ServiceAlternativesBadge'
import EmailTemplateManager from './EmailTemplateManager'
import VideoRecorderEnhanced from './VideoRecorderEnhanced'
import RichTextEditor from './RichTextEditor'
import SalesCompass from './SalesCompass'
import CoachingWidget from './CoachingWidget'
import SalesToolsDashboard from './SalesToolsDashboard'
import CustomerNeedsForm from './CustomerNeedsForm'
import LiveCoaching from './LiveCoaching'
import Dashboard from './Dashboard'
import WorkflowNavigator from './WorkflowNavigator'
import QuoteTermsManager from './QuoteTermsManager'
import QuoteSharingStep from './QuoteSharingStep'
import { CalculatorProvider } from './CalculatorContext'
import ApiKeyManager from './ApiKeyManager'
import PricingSidebar from './PricingSidebar'
import AccountSettings from './AccountSettings'
import DocumentManager from './DocumentManager'
import ServiceCalculatorCore from './ServiceCalculatorCore'
import { companiesApi, serviceItemsApi, servicePackagesApi, quotesApi, customersApi } from '../services/api'

const Calculator = ({ user, onLogout }) => {
  // 4-Step Workflow state
  const [activeTab, setActiveTab] = useState('dashboard')  // Start mit Firma
  const [activeCompany, setActiveCompany] = useState(null)
  
  // Original state structure based on QuoteManager expectations
  const [customerInfo, setCustomerInfo] = useState({
    company: '',
    name: '',
    address: '',
    city: '',
    phone: '',
    email: ''
  })
  
  // Original calculator state
  const [subpageCount, setSubpageCount] = useState(3)
  const [selectedServices, setSelectedServices] = useState({})
  const [selectedPackages, setSelectedPackages] = useState([])
  const [discount, setDiscount] = useState({
    oneTime: { type: 'percentage', value: 0, reason: '' }, 
    monthly: { type: 'percentage', value: 0, reason: '' },
    yearly: { type: 'percentage', value: 0, reason: '' }
  })
  const [savedQuoteId, setSavedQuoteId] = useState(null)
  const [savedQuoteNumber, setSavedQuoteNumber] = useState(null)
  const [savedViewHash, setSavedViewHash] = useState(null)
  
  // Zahlungsbedingungen state
  const [paymentTerms, setPaymentTerms] = useState('30-tage-netto')
  const [customPaymentTerms, setCustomPaymentTerms] = useState('')
  const [savedCustomTerms, setSavedCustomTerms] = useState([]) // Für gespeicherte benutzerdefinierte Bedingungen
  
  // Neue Felder für Lieferzeit, Gültigkeit, Gewährleistung
  const [deliveryTime, setDeliveryTime] = useState('4-6 Wochen nach Auftragserteilung')
  const [validity, setValidity] = useState('30 Tage gültig ab Erstellungsdatum')
  const [warranty, setWarranty] = useState('')
  
  // Projektbeschreibung & KI-Intro
  const [additionalInfo, setAdditionalInfo] = useState('')
  const [quoteIntro, setQuoteIntro] = useState('')
  const [heroHeadline, setHeroHeadline] = useState('')
  const [heroSubline, setHeroSubline] = useState('')
  const [nextSteps, setNextSteps] = useState('')
  const [customerArguments, setCustomerArguments] = useState('')
  const [includeDetailedDescriptions, setIncludeDetailedDescriptions] = useState(false)
  
  // KI-Dialog States
  const [isIntroDialogOpen, setIsIntroDialogOpen] = useState(false)
  const [isGeneratingIntro, setIsGeneratingIntro] = useState(false)
  const [introFormData, setIntroFormData] = useState({
    company_name: '',
    industry: '',
    contact_person: '',
    project_goal: '',
    website_url: '',
    current_status: '',
    challenges: '',
    budget: '',
    timeline: '',
    special_requirements: '',
    tone: 'professional',
    custom_project_goal: '',
    custom_current_status: ''
  })
  const [tempGeneratedIntro, setTempGeneratedIntro] = useState('')
  const [tempAdditionalInfo, setTempAdditionalInfo] = useState('')
  
  // Quote sections state
  const [quoteSections, setQuoteSections] = useState([])
  
  // Email template state
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(null)
  
  // Video state
  const [videoUrl, setVideoUrl] = useState(null)
  
  // Passwortschutz state
  const [passwordProtected, setPasswordProtected] = useState(false)
  const [customPassword, setCustomPassword] = useState('')
  
  // Sales Coaching state
  const [salesContext, setSalesContext] = useState(() => {
    try { return JSON.parse(localStorage.getItem('salesContext') || 'null') } catch { return null }
  })
  const [customerNeeds, setCustomerNeeds] = useState(null)
  const [allQuotes, setAllQuotes] = useState([])
  
  // Management state
  const [showManagement, setShowManagement] = useState(false)
  const [managementType, setManagementType] = useState('services')
  
  // Collapsible state for Rabatte and Zahlungsbedingungen
  const [isRabatteOpen, setIsRabatteOpen] = useState(false)
  const [isZahlungsbedingungenOpen, setIsZahlungsbedingungenOpen] = useState(false)

  const openManagement = (type) => {
    if (type === 'dashboard') {
      setShowManagement(false)
      setActiveTab('dashboard')
      return
    }
    if (type === 'apikeys') {
      setShowManagement(false)
      setActiveTab('apikeys')
      return
    }
    if (type === 'documents') {
      setShowManagement(false)
      setActiveTab('documents')
      return
    }
    if (type === 'account') {
      setShowManagement(false)
      setActiveTab('account')
      return
    }
    setManagementType(type)
    setShowManagement(true)
    setActiveTab('management')
  }

  const closeManagement = () => {
    setShowManagement(false)
    setActiveTab('kalkulator')
  }
  
  // Data state
  const [companies, setCompanies] = useState([])
  const [services, setServices] = useState([])
  const [packages, setPackages] = useState([])
  
  // Custom position state
  const [isCustomPositionOpen, setIsCustomPositionOpen] = useState(false)
  const [customPosition, setCustomPosition] = useState({
    name: '',
    description: '',
    detailedDescription: '',
    price: '',
    quantity: 1,
    unit: 'Stk.',
    type: 'einmalig',
    category: 'Sonstige',
    discountValue: '',
    discountType: 'percentage',
    saveForFuture: false
  })
  
  // Collapse state for categories and services (default: all collapsed)
  const [collapsedCategories, setCollapsedCategories] = useState({})
  const [expandedServices, setExpandedServices] = useState({})
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchFilters, setSearchFilters] = useState({
    type: 'all', // all, einmalig, monatlich, jährlich
    priceRange: 'all', // all, under-100, 100-500, 500-1000, over-1000
    category: 'all' // all, or specific category
  })
  
  // Unsaved changes dialog state
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  
  // Alternative Positions state
  const [alternativesDialogOpen, setAlternativesDialogOpen] = useState(false)
  const [currentAlternativeService, setCurrentAlternativeService] = useState(null)
  
  // Category map for normalizing category IDs/names
  const [categoryMap, setCategoryMap] = useState({})
  const [categoryOrder, setCategoryOrder] = useState([])
  
  // Initialize all categories as collapsed when services load
  useEffect(() => {
    if (services.length > 0 && Object.keys(categoryMap).length > 0) {
      const initialState = {}
      // Get all unique categories from services (normalized to category IDs)
      services.forEach(service => {
        let category = service.category || 'other'
        // Normalize: if it's a name, find the matching ID
        const matchingCatId = Object.keys(categoryMap).find(catId => 
          categoryMap[catId]?.name === category
        )
        if (matchingCatId) {
          category = matchingCatId
        }
        initialState[category] = true // true = collapsed
      })
      setCollapsedCategories(initialState)
    }
  }, [services, categoryMap]) // Run when services or categoryMap change

  // Load initial data
  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [companiesData, servicesData, packagesData] = await Promise.all([
        companiesApi.getAll(),
        serviceItemsApi.getAll(),
        servicePackagesApi.getAll()
      ])
      
      setCompanies(companiesData.data || [])
      setServices(servicesData.data || [])
      setPackages(packagesData.data || [])
      
      // Set default company
      const defaultCompany = (companiesData.data || []).find(c => c.isDefault) || (companiesData.data || [])[0]
      if (defaultCompany) {
        setActiveCompany(defaultCompany)
        console.log('🔍 Set activeCompany:', defaultCompany)
      }
      
      // Reload categories to get updated order
      if (loadCategories) loadCategories()
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Fehler beim Laden der Daten')
    }
  }

  // Calculate totals based on original structure
  const currentQuote = useMemo(() => {
    return {
      id: savedQuoteId,
      customerInfo,
      customerData: customerInfo,
      subpageCount,
      selectedServices,
      selectedPackages,
      discount,
      paymentTerms,
      customPaymentTerms,
      quoteIntro,
      heroHeadline,
      heroSubline,
      additionalInfo,
      deliveryTime,
      validity,
      warranty,
      includeDetailedDescriptions,
      companyId: activeCompany?.id || 'default_company'
    }
  }, [
    savedQuoteId,
    customerInfo,
    subpageCount,
    selectedServices,
    selectedPackages,
    discount,
    paymentTerms,
    customPaymentTerms,
    quoteIntro,
    heroHeadline,
    heroSubline,
    additionalInfo,
    deliveryTime,
    validity,
    warranty,
    includeDetailedDescriptions,
    activeCompany
  ])

  // Load saved payment terms from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('customPaymentTerms')
      if (saved) {
        setSavedCustomTerms(JSON.parse(saved))
      }
    } catch (error) {
      console.error('Error loading saved payment terms:', error)
    }
  }, [])

  const saveCustomPaymentTerm = () => {
    if (customPaymentTerms && !savedCustomTerms.includes(customPaymentTerms)) {
      const newTerms = [...savedCustomTerms, customPaymentTerms]
      setSavedCustomTerms(newTerms)
      
      // Save to localStorage
      try {
        localStorage.setItem('customPaymentTerms', JSON.stringify(newTerms))
        toast.success('Zahlungsbedingung für Zukunft gespeichert')
      } catch (error) {
        console.error('Error saving payment terms:', error)
        toast.error('Fehler beim Speichern')
      }
    }
  }

  // Industry and project goal options for KI-Dialog
  const industries = [
    'Webdesign', 'Friseur/Beauty', 'Rechtsberatung', 'Gastronomie', 'Immobilien', 
    'IT-Beratung', 'Marketing', 'Gesundheitswesen', 'Einzelhandel', 'Handwerk',
    'Beratung', 'Finanzdienstleistungen', 'Bildung', 'Automotive', 'Wellness'
  ]

  const getProjectGoalsByIndustry = (industry) => {
    const goals = {
      'Webdesign': ['Neue Website erstellen', 'Bestehende Website modernisieren', 'Online-Shop einrichten', 'Suchmaschinenoptimierung', 'Mobile Optimierung', 'Eigene Eingabe'],
      'Friseur/Beauty': ['Online-Buchungssystem einrichten', 'Moderne Website für Salon', 'Local SEO für mehr Kunden', 'Before/After Galerie erstellen', 'Social Media Integration', 'Eigene Eingabe'],
      'Gastronomie': ['Online-Reservierungssystem', 'Speisekarte digital präsentieren', 'Lieferservice-Website', 'Event-Buchungen ermöglichen', 'Local SEO für Restaurant', 'Eigene Eingabe'],
      'Immobilien': ['Immobilien-Portal erstellen', 'Virtual Tours integrieren', 'Lead-Generierung optimieren', 'Property Management System', 'SEO für Immobilienmakler', 'Eigene Eingabe']
    }
    return goals[industry] || ['Digitale Präsenz aufbauen', 'Online-Sichtbarkeit erhöhen', 'Kundengewinnung digitalisieren', 'Prozesse digitalisieren', 'Marktposition stärken', 'Eigene Eingabe']
  }

  const getCurrentStatusByIndustry = (industry) => {
    const statuses = {
      'Webdesign': ['Keine Website vorhanden', 'Veraltete Website', 'Website modernisieren', 'Neue Funktionen hinzufügen', 'Eigene Eingabe'],
      'Friseur/Beauty': ['Keine Online-Buchung', 'Veraltete Salon-Website', 'Kein Local SEO', 'Fehlende Mobile-Optimierung', 'Eigene Eingabe'],
      'Gastronomie': ['Keine Online-Reservierung', 'Veraltete Restaurant-Website', 'Keine Speisekarte online', 'Fehlende Lieferservice-Integration', 'Eigene Eingabe'],
      'Immobilien': ['Keine Immobilien-Website', 'Veraltetes Exposé-System', 'Keine Virtual Tours', 'Fehlende Lead-Generierung', 'Eigene Eingabe']
    }
    return statuses[industry] || ['Keine Website vorhanden', 'Veraltete Website', 'Modernisierung benötigt', 'Neue Funktionen hinzufügen', 'Eigene Eingabe']
  }

  const generateAIIntro = async () => {
    if (!introFormData.company_name || !introFormData.industry || !introFormData.current_status) {
      toast.error('Bitte füllen Sie mindestens Firmenname, Branche und aktuellen Status aus.')
      return
    }

    setIsGeneratingIntro(true)
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || ''
      
      // Get selected services info for context
      const selectedServicesList = Object.values(selectedServices || {})
      const serviceNames = selectedServicesList.map(s => s.name || s.id).join(', ')
      const serviceCount = selectedServicesList.length
      
      const pricing = calculatePricing
      
      // Enhanced request with service context
      const introRequest = {
        ...introFormData,
        // Add service context
        selected_services: serviceNames,
        service_count: serviceCount,
        project_value: pricing?.total || 0,
        // Use custom entries if "Eigene Eingabe" was selected
        project_goal: introFormData.project_goal === 'Eigene Eingabe' ? 
          introFormData.custom_project_goal || introFormData.project_goal : 
          introFormData.project_goal,
        current_status: introFormData.current_status === 'Eigene Eingabe' ? 
          introFormData.custom_current_status || introFormData.current_status : 
          introFormData.current_status
      }

      console.log('🔍 Generating intro with services context:', introRequest)

      const response = await fetch(`${BACKEND_URL}/api/quote-intro/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(introRequest)
      })

      if (!response.ok) {
        throw new Error('Fehler bei der KI-Generierung')
      }

      const data = await response.json()
      console.log('🔍 Intro API response:', data)
      
      // Handle different response formats from backend
      const introText = data.intro_text || data.intro || data.data?.intro_text || ''
      const additionalText = data.additional_info || data.additionalInfo || data.data?.additional_info || ''
      
      if (introText && introText.length > 50) {
        setTempGeneratedIntro(introText)
        setTempAdditionalInfo(additionalText)
        toast.success(`✨ Intro erfolgreich generiert (${introText.length} Zeichen)!`)
      } else {
        console.log('Response data:', data)
        toast.error('Die KI hat keine verwertbare Antwort gesendet. Bitte versuchen Sie es mit anderen Daten.')
      }
    } catch (error) {
      console.error('Error generating intro:', error)
      toast.error(`Fehler: ${error.message || 'Unbekannter Fehler'}`)
    } finally {
      setIsGeneratingIntro(false)
    }
  }

  const handleUseIntro = () => {
    setQuoteIntro(tempGeneratedIntro)
    setAdditionalInfo(tempAdditionalInfo)
    setIsIntroDialogOpen(false)
    toast.success('Intro übernommen!')
  }

  const handleResetForm = () => {
    setIntroFormData({
      company_name: customerInfo?.company || '',
      industry: '',
      contact_person: customerInfo?.name || '',
      project_goal: '',
      website_url: '',
      current_status: '',
      challenges: '',
      budget: '',
      timeline: '',
      special_requirements: '',
      tone: 'professional',
      custom_project_goal: '',
      custom_current_status: ''
    })
    setTempGeneratedIntro('')
    setTempAdditionalInfo('')
  }
  
  // Update introFormData when customerInfo changes
  useEffect(() => {
    setIntroFormData(prev => ({
      ...prev,
      company_name: customerInfo?.company || '',
      contact_person: customerInfo?.name || ''
    }))
  }, [customerInfo])

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }
  
  const toggleServiceDetails = (serviceId) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }))
  }
  
  const handleAddCustomPosition = async () => {
    if (!customPosition.name || !customPosition.price) {
      toast.error('❌ Bitte Name und Preis eingeben')
      return
    }

    const price = parseFloat(customPosition.price)
    if (isNaN(price) || price < 0) {
      toast.error('❌ Bitte gültigen Preis eingeben')
      return
    }

    // Check if editing existing position
    const isEditing = customPosition.editingId
    
    // Create/update service object
    const discountValue = parseFloat(customPosition.discountValue) || 0
    const newService = {
      id: isEditing ? customPosition.editingId : `custom-${Date.now()}`,
      name: customPosition.name,
      description: customPosition.description,
      detailedDescription: customPosition.detailedDescription,
      price: price,
      originalPrice: price,
      quantity: customPosition.quantity || 1,
      unit: customPosition.unit || 'Stk.',
      type: customPosition.type,
      category: customPosition.category,
      discountValue: discountValue,
      discountType: customPosition.discountType || 'percentage',
      isCustom: true,
      companyId: activeCompany?.id || 'default_company'
    }

    // Save to database if requested
    if (customPosition.saveForFuture) {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || ''
        const response = await fetch(`${BACKEND_URL}/api/service-items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newService)
        })

        if (response.ok) {
          const savedService = await response.json()
          // Update local services list
          setServices(prev => [...prev, savedService])
          // Use saved service with DB id
          newService.id = savedService.id
          toast.success('✅ Position gespeichert und hinzugefügt')
        } else {
          toast.error('⚠️ Position nur für dieses Angebot hinzugefügt (nicht gespeichert)')
        }
      } catch (error) {
        console.error('Error saving custom position:', error)
        toast.error('⚠️ Position nur für dieses Angebot hinzugefügt (nicht gespeichert)')
      }
    } else {
      toast.success(isEditing ? '✅ Position aktualisiert' : '✅ Position hinzugefügt')
    }

    // Add/update in selected services
    setSelectedServices(prev => ({
      ...prev,
      [newService.id]: newService
    }))

    // Reset form
    setCustomPosition({
      name: '',
      description: '',
      detailedDescription: '',
      price: '',
      quantity: 1,
      unit: 'Stk.',
      type: 'einmalig',
      category: 'Sonstige',
      saveForFuture: false
    })
    setIsCustomPositionOpen(false)
  }

  const handlePackageToggle = (packageId) => {
    const pkg = packages.find(p => p.id === packageId)
    if (!pkg) return
    
    setSelectedPackages(prev => {
      const newPackages = { ...prev }
      const isCurrentlySelected = !!newPackages[packageId]
      
      if (isCurrentlySelected) {
        // Remove package
        delete newPackages[packageId]
        
        // Remove all services from this package
        setSelectedServices(prevServices => {
          const updatedServices = { ...prevServices }
          Object.keys(updatedServices).forEach(serviceId => {
            if (updatedServices[serviceId].packageId === packageId) {
              delete updatedServices[serviceId]
            }
          })
          return updatedServices
        })
      } else {
        // Add package
        newPackages[packageId] = pkg
        
        // Add all services from this package with package info
        if (pkg.serviceIds && pkg.serviceIds.length > 0) {
          setSelectedServices(prevServices => {
            const updatedServices = { ...prevServices }
            
            pkg.serviceIds.forEach(serviceId => {
              // Find the service in services state
              const service = services.find(s => s.id === serviceId)
              if (service) {
                updatedServices[serviceId] = {
                  ...service,
                  packageId: packageId,
                  packageName: pkg.name,
                  fromPackage: true
                }
              }
            })
            
            return updatedServices
          })
        }
      }
      return newPackages
    })
  }
  
  // Handler für Alternative Positionen
  const handleOpenAlternatives = (service) => {
    const serviceData = selectedServices[service.id] || service
    setCurrentAlternativeService({
      ...service,
      alternatives: serviceData.alternatives || []
    })
    setAlternativesDialogOpen(true)
  }
  
  const handleSaveAlternatives = (alternatives) => {
    if (!currentAlternativeService) return
    
    const serviceId = currentAlternativeService.id
    
    setSelectedServices(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        alternatives: alternatives,
        // Set default selectedAlternativeId to the first default alternative or first alternative
        selectedAlternativeId: alternatives.find(a => a.isDefault)?.id || alternatives[0]?.id || null
      }
    }))
    
    toast.success(`${alternatives.length} Alternative${alternatives.length !== 1 ? 'n' : ''} gespeichert`)
  }
  
  const handleSelectAlternative = (serviceId, alternativeId) => {
    setSelectedServices(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        selectedAlternativeId: alternativeId
      }
    }))
  }

  const companyId = activeCompany?.id || 'default_company'
  
  // Get company colors with defaults
  const getCompanyColors = () => {
    return {
      einmalig: activeCompany?.colorEinmalig || '#22c55e',
      monatlich: activeCompany?.colorMonatlich || '#f97316',
      jaehrlich: activeCompany?.colorJaehrlich || '#a855f7',
      primary: activeCompany?.primaryColor || '#3b82f6',
      secondary: activeCompany?.secondaryColor || '#8b5cf6',
      accent: activeCompany?.accentColor || '#f59e0b'
    }
  }
  
  const companyColors = getCompanyColors()
  
  // Debug logging
  useEffect(() => {
    console.log('🔍 DEBUG: activeCompany:', activeCompany)
    console.log('🔍 DEBUG: companyId for quotes:', companyId)
    console.log('🔍 DEBUG: companyColors:', companyColors)
  }, [activeCompany, companyId])

  // Service categories
  // Group services by category, normalizing category names to IDs when possible
  const servicesByCategory = useMemo(() => {
    return services.reduce((acc, service) => {
      let category = service.category || 'other'
      
      // Normalize: If it's a name that matches a category's name, use the ID instead
      if (Object.keys(categoryMap).length > 0) {
        const matchingCatId = Object.keys(categoryMap).find(catId => 
          categoryMap[catId]?.name === category
        )
        if (matchingCatId) {
          category = matchingCatId
        }
      }
      
      if (!acc[category]) acc[category] = []
      acc[category].push(service)
      return acc
    }, {})
  }, [services, categoryMap])
  
  // Filter services based on search and filters
  const filteredServicesByCategory = useMemo(() => {
    if (!searchQuery && searchFilters.type === 'all' && searchFilters.priceRange === 'all' && searchFilters.category === 'all') {
      return servicesByCategory
    }
    
    const filtered = {}
    
    Object.entries(servicesByCategory).forEach(([category, categoryServices]) => {
      // Filter by category first
      if (searchFilters.category !== 'all' && searchFilters.category !== category) {
        return
      }
      
      const matchingServices = categoryServices.filter(service => {
        // Text search (name + description)
        if (searchQuery) {
          const query = searchQuery.toLowerCase()
          const matchesText = 
            service.name.toLowerCase().includes(query) ||
            (service.description && service.description.toLowerCase().includes(query))
          
          if (!matchesText) return false
        }
        
        // Type filter
        if (searchFilters.type !== 'all' && service.type !== searchFilters.type) {
          return false
        }
        
        // Price range filter
        if (searchFilters.priceRange !== 'all') {
          const price = service.price || 0
          switch (searchFilters.priceRange) {
            case 'under-100':
              if (price >= 100) return false
              break
            case '100-500':
              if (price < 100 || price >= 500) return false
              break
            case '500-1000':
              if (price < 500 || price >= 1000) return false
              break
            case 'over-1000':
              if (price < 1000) return false
              break
          }
        }
        
        return true
      })
      
      if (matchingServices.length > 0) {
        filtered[category] = matchingServices
      }
    })
    
    return filtered
  }, [servicesByCategory, searchQuery, searchFilters])

  const getCategoryIcon = (category) => {
    // Check if it's a UUID (new format) and we have a mapping
    if (categoryMap[category]) {
      return categoryMap[category].icon
    }
    
    // Fallback to old hardcoded icons for backward compatibility
    const icons = {
      'webdesign': '🌐',
      'ecommerce': '🛒', 
      'photography': '📷',
      'design': '🎨',
      'maintenance': '🔧',
      'logo': '🎨',
      'forms': '📝',
      'seo': '🔍',
      'sea': '📢',
      'technical': '⚙️',
      'other': '⚙️'
    }
    return icons[category] || '⚙️'
  }

  // Load categories from API
  const loadCategories = React.useCallback(async () => {
    if (!companyId) return
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || ''
      const response = await fetch(`${BACKEND_URL}/api/categories/${companyId}`)
      if (response.ok) {
        const cats = await response.json()
        // Sort by order field
        const sortedCats = cats.sort((a, b) => (a.order || 0) - (b.order || 0))
        
        const map = {}
        const order = []
        sortedCats.forEach(cat => {
          map[cat.id] = { name: cat.name, icon: cat.icon, order: cat.order }
          order.push(cat.id)
        })
        setCategoryMap(map)
        setCategoryOrder(order)
        console.log('🔍 [Calculator] Loaded categories in order:', order)
      }
    } catch (error) {
      console.error('Error loading categories in Calculator:', error)
    }
  }, [companyId])
  
  React.useEffect(() => {
    loadCategories()
  }, [companyId, loadCategories])
  
  const getCategoryName = (category) => {
    // Check if it's a UUID (new format) and we have a mapping
    if (categoryMap[category]) {
      return categoryMap[category].name
    }
    
    // Fallback to old hardcoded names for backward compatibility
    const names = {
      'webdesign': 'Webdesign',
      'ecommerce': 'E-Commerce',
      'photography': 'Fotografie',
      'design': 'Design',
      'maintenance': 'Wartung',
      'logo': 'Logo & Branding',
      'forms': 'Formulare',
      'seo': 'SEO & Marketing', 
      'sea': 'Google Ads',
      'technical': 'Technische Features',
      'other': 'Sonstiges'
    }
    return names[category] || category
  }

  // Calculate total for pricing overview with discounts (excluding optional services)
  const calculatePricing = useMemo(() => {
    const allServices = Object.values(selectedServices)
    const includedServices = allServices.filter(s => !s.isOptional) // Exclude optional services from total
    
    const einmaligServices = includedServices.filter(s => s.type === 'einmalig')
    const monatlichServices = includedServices.filter(s => s.type === 'monatlich' || s.type === 'variabel')
    const jaehrlichServices = includedServices.filter(s => s.type === 'jährlich')
    
    // Calculate subtotals with individual service discounts and alternatives
    const calculateServiceTotal = (service) => {
      // WICHTIG: In Schritt 3/4 wird IMMER die Hauptposition berechnet
      // Die Alternative wird nur als Option angezeigt, aber NICHT im Preis
      // Nur auf der Landingpage (PublicQuoteView) kann der Kunde die Alternative wählen
      let effectivePrice = service.price || service.originalPrice
      const qty = service.quantity || 1
      
      // LEGACY: Support für altes Alternative-System (falls noch vorhanden)
      if (service.alternatives && service.alternatives.length > 0 && service.selectedAlternativeId) {
        const selectedAlt = service.alternatives.find(a => a.id === service.selectedAlternativeId)
        if (selectedAlt) {
          effectivePrice = selectedAlt.price
        }
      }
      
      // Staffelpreise: Finde den passenden Tier für die Menge
      let basePrice
      if (service.priceTiers && service.priceTiers.length > 0) {
        const activeTier = service.priceTiers.find(t => t.upTo === null || qty <= t.upTo)
          || service.priceTiers[service.priceTiers.length - 1]
        basePrice = (activeTier.pricePerUnit ?? effectivePrice) * qty
      } else {
        basePrice = effectivePrice * qty
      }
      
      // Apply individual service discount
      if (service.customDiscount && service.customDiscount.value > 0) {
        if (service.customDiscount.type === 'percentage') {
          return basePrice * (1 - service.customDiscount.value / 100)
        } else {
          return Math.max(0, basePrice - service.customDiscount.value)
        }
      }
      
      return basePrice
    }
    
    // Packages are now expanded into their services, so no separate package calculation needed
    let einmaligSubtotal = einmaligServices.reduce((sum, service) => sum + calculateServiceTotal(service), 0)
    let monatlichSubtotal = monatlichServices.reduce((sum, service) => sum + calculateServiceTotal(service), 0)
    let jaehrlichSubtotal = jaehrlichServices.reduce((sum, service) => sum + calculateServiceTotal(service), 0)
    
    // Apply global discounts - separate for einmalig, monatlich, and jährlich
    let einmaligDiscount = 0
    let monatlichDiscount = 0
    let jaehrlichDiscount = 0
    
    // Einmalige Rabatte
    if (discount.oneTime?.value > 0) {
      if (discount.oneTime.type === 'percentage') {
        einmaligDiscount = einmaligSubtotal * (discount.oneTime.value / 100)
      } else {
        einmaligDiscount = Math.min(discount.oneTime.value, einmaligSubtotal)
      }
    }
    
    // Monatliche Rabatte
    if (discount.monthly?.value > 0) {
      if (discount.monthly.type === 'percentage') {
        monatlichDiscount = monatlichSubtotal * (discount.monthly.value / 100)
      } else {
        monatlichDiscount = Math.min(discount.monthly.value, monatlichSubtotal)
      }
    }
    
    // Jährliche Rabatte (NEU - separate von monatlich)
    if (discount.yearly?.value > 0) {
      if (discount.yearly.type === 'percentage') {
        jaehrlichDiscount = jaehrlichSubtotal * (discount.yearly.value / 100)
      } else {
        jaehrlichDiscount = Math.min(discount.yearly.value, jaehrlichSubtotal)
      }
    }
    
    const einmaligTotal = Math.max(0, einmaligSubtotal - einmaligDiscount)
    const monatlichTotal = Math.max(0, monatlichSubtotal - monatlichDiscount)
    const jaehrlichTotal = Math.max(0, jaehrlichSubtotal - jaehrlichDiscount)
    
    return {
      einmaligSubtotal,
      monatlichSubtotal,
      jaehrlichSubtotal,
      einmaligDiscount,
      monatlichDiscount,
      jaehrlichDiscount,
      einmaligTotal,
      monatlichTotal,
      jaehrlichTotal,
      laufendSubtotal: monatlichSubtotal + jaehrlichSubtotal,
      laufendDiscount: monatlichDiscount + jaehrlichDiscount,
      laufendTotal: monatlichTotal + jaehrlichTotal,
      total: einmaligTotal, // NUR einmalige Kosten in Gesamtsumme!
      monthlyTotal: monatlichTotal, // Monatliche Kosten separat
      yearlyTotal: jaehrlichTotal, // Jährliche Kosten separat
      optionalServices: allServices.filter(s => s.isOptional)
    }
  }, [selectedServices, selectedPackages, discount])

  // Tab navigation helpers
  const salesCoachingEnabled = activeCompany?.salesCoachingEnabled === true

  const tabConfig = {
    ...(salesCoachingEnabled ? {
      'kompass': { icon: Target, title: 'Sales-Kompass', step: 0 }
    } : {}),
    'firma': { icon: BuildingOfficeIcon, title: 'Branding wählen', step: 1 },
    'kunde': { icon: UserIcon, title: 'Kunde wählen', step: 2 },
    'kalkulator': { icon: CalculatorIcon, title: 'Kalkulator', step: 3 },
    'pdf': { icon: DocumentIcon, title: 'Angebot Vorschau', step: 4 },
    'teilen': { icon: ChevronRightIcon, title: 'Teilen', step: 5 }
  }

  const getTabStatus = (tabKey) => {
    switch (tabKey) {
      case 'kompass':
        return salesContext ? 'complete' : 'active'
      case 'firma':
        return activeCompany ? 'complete' : 'active'
      case 'kunde':
        return customerInfo.company && customerInfo.name && customerInfo.email ? 'complete' : 'active'
      case 'kalkulator':
        return Object.keys(selectedServices).length > 0 ? 'complete' : 'active'  
      case 'pdf':
        return Object.keys(selectedServices).length > 0 ? 'complete' : 'active'
      case 'teilen':
        return 'active'
      default:
        return 'inactive'
    }
  }

  const canAccessTab = (tabKey) => {
    switch (tabKey) {
      case 'kompass':
        return true
      case 'firma':
        return true
      case 'kunde':  
        return !!activeCompany
      case 'kalkulator':
        return !!(activeCompany && customerInfo.company && customerInfo.name && customerInfo.email)
      case 'pdf':
        return !!(activeCompany && customerInfo.company && customerInfo.name && customerInfo.email)
      case 'teilen':
        return !!(activeCompany && customerInfo.company && customerInfo.name && customerInfo.email && Object.keys(selectedServices).length > 0)
      default:
        return false
    }
  }
  
  // Handle quote load from Quote Manager
  const handleQuoteLoad = (quote) => {
    console.log('🔍 Loading quote:', quote)
    
    // Load customer info
    if (quote.customerData) {
      setCustomerInfo(quote.customerData)
    }
    
    // Transform selectedServices from Array to Object
    if (quote.selectedServices) {
      const servicesObject = {}
      
      if (Array.isArray(quote.selectedServices)) {
        quote.selectedServices.forEach(savedService => {
          const serviceId = savedService.serviceId
          const fullServiceData = services.find(s => s.id === serviceId)
          
          if (fullServiceData) {
            servicesObject[serviceId] = {
              ...fullServiceData,
              price: savedService.customPrice !== null && savedService.customPrice !== undefined 
                ? savedService.customPrice 
                : fullServiceData.price,
              originalPrice: fullServiceData.price,
              notes: savedService.customDescription || '',
              isOptional: savedService.isOptional || false,
              quantity: savedService.quantity || 1,
              customDiscount: savedService.customDiscount || null,
              alternatives: savedService.alternatives || [],
              selectedAlternativeId: savedService.selectedAlternativeId || null
            }
          }
        })
        setSelectedServices(servicesObject)
      }
    }
    
    // Load other quote data
    if (quote.subpageCount) setSubpageCount(quote.subpageCount)
    if (quote.discount) {
      // Ensure backwards compatibility - add yearly if missing
      setDiscount({
        oneTime: quote.discount.oneTime || { type: 'percentage', value: 0, reason: '' },
        monthly: quote.discount.monthly || { type: 'percentage', value: 0, reason: '' },
        yearly: quote.discount.yearly || { type: 'percentage', value: 0, reason: '' }
      })
    }
    if (quote.paymentTerms) setPaymentTerms(quote.paymentTerms)
    if (quote.customPaymentTerms) setCustomPaymentTerms(quote.customPaymentTerms)
    if (quote.deliveryTime) setDeliveryTime(quote.deliveryTime)
    if (quote.validity) setValidity(quote.validity)
    if (quote.warranty) setWarranty(quote.warranty)
    if (quote.quoteIntro !== undefined) setQuoteIntro(quote.quoteIntro || '')
    if (quote.heroHeadline !== undefined) setHeroHeadline(quote.heroHeadline || '')
    if (quote.heroSubline !== undefined) setHeroSubline(quote.heroSubline || '')
    if (quote.additionalInfo !== undefined) setAdditionalInfo(quote.additionalInfo || '')
    if (quote.nextSteps !== undefined) setNextSteps(quote.nextSteps || '')
    if (quote.customerArguments !== undefined) setCustomerArguments(quote.customerArguments || '')
    if (quote.includeDetailedDescriptions !== undefined) setIncludeDetailedDescriptions(quote.includeDetailedDescriptions)
    if (quote.quoteSections) setQuoteSections(quote.quoteSections)
    if (quote.id) setSavedQuoteId(quote.id)
    if (quote.quoteNumber) setSavedQuoteNumber(quote.quoteNumber)
    if (quote.viewHash) setSavedViewHash(quote.viewHash)  // Auto-populate link for existing quotes
    
    // Switch to calculator tab
    setActiveTab('kalkulator')
    toast.success('Angebot geladen')
  }
  
  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    // Check if we have any services selected or customer info filled
    const hasServices = Object.keys(selectedServices).length > 0
    const hasCustomerData = customerInfo.company || customerInfo.name || customerInfo.email
    const isInWorkflow = ['kunde', 'kalkulator', 'pdf-vorschau'].includes(activeTab)
    
    // If we're in the workflow, have data, but no saved quote ID = unsaved changes
    return isInWorkflow && (hasServices || hasCustomerData) && !savedQuoteId
  }
  
  // Safe tab change with warning
  const handleTabChangeWithWarning = (newTab) => {
    // If leaving steps 3 or 4 with unsaved changes (NOT step 2 - too early)
    if (hasUnsavedChanges() && ['kalkulator', 'pdf-vorschau'].includes(activeTab)) {
      setPendingNavigation(newTab)
      setShowUnsavedDialog(true)
      return false
    }
    
    setActiveTab(newTab)
    return true
  }
  
  // Handle navigation from Top Nav
  const handleTopNavigation = (type) => {
    // Direct tab navigation (no management overlay needed)
    if (type === 'dashboard') {
      setActiveTab('dashboard')
      return
    }
    if (type === 'account') {
      setShowManagement(false)
      setActiveTab('account')
      return
    }
    if (type === 'apikeys') {
      setShowManagement(false)
      setActiveTab('apikeys')
      return
    }
    if (type === 'documents') {
      setShowManagement(false)
      setActiveTab('documents')
      return
    }
    if (hasUnsavedChanges()) {
      setPendingNavigation(() => () => {
        setManagementType(type)
        setActiveTab('management')
      })
      setShowUnsavedDialog(true)
      return
    }
    
    setManagementType(type)
    setActiveTab('management')
    
    // Load quotes for Vertrieb dashboard
    if (type === 'vertrieb' && companyId) {
      quotesApi.getAll(companyId).then(r => setAllQuotes(r.data || [])).catch(() => {})
    }
  }
  
  // Save quote ref to access QuoteManager's save function
  const quoteManagerRef = React.useRef(null)
  
  // Handle save quote from Top Nav - Direct save without navigation
  const handleSaveQuoteFromNav = async () => {
    if (!companyId) {
      toast.error('Bitte wählen Sie zuerst Ihre Firma (Absender) aus.')
      return
    }
    if (!customerInfo?.company) {
      toast.error('Bitte geben Sie mindestens den Firmennamen des Kunden ein.')
      return
    }
    if (Object.keys(selectedServices).length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Leistung aus.')
      return
    }

    try {
      // 1) Find or create customer
      let customerId = null
      const searchQuery = customerInfo.email || `${customerInfo.company} ${customerInfo.name || ''}`.trim()
      try {
        if (searchQuery) {
          const res = await customersApi.search(searchQuery, companyId, 1)
          const found = res?.data || []
          if (Array.isArray(found) && found.length > 0) {
            customerId = found[0].id
          }
        }
      } catch (_) {}

      if (!customerId) {
        try {
          const createRes = await customersApi.create({
            company: customerInfo.company || '',
            name: customerInfo.name || '',
            address: customerInfo.address || '',
            city: customerInfo.city || '',
            phone: customerInfo.phone || '',
            email: customerInfo.email || '',
            companyId: companyId,
          })
          customerId = createRes?.data?.id
        } catch (_) {}
      }

      // 2) Map services
      const mappedServices = Object.entries(selectedServices).map(([key, service]) => ({
        serviceId: service.id,
        customPrice: service.price !== service.originalPrice ? service.price : null,
        customDescription: service.notes || null,
        isOptional: !!service.isOptional,
        isIncluded: !service.isOptional,
        quantity: service.quantity || 1,
        customDiscount: service.customDiscount || null,
        basePrice: service.originalPrice || service.price || 0,
        finalPrice: service.price || service.originalPrice || 0,
        alternatives: service.alternatives || [],
        selectedAlternativeId: service.selectedAlternativeId || null,
        hasAlternative: service.hasAlternative || false,
        alternativeName: service.alternativeName || null,
        alternativePrice: service.alternativePrice || null,
        alternativeDescription: service.alternativeDescription || null,
        alternativeSelected: service.alternativeSelected || false
      }))

      // 3) Build quote payload
      const quoteData = {
        companyId,
        customerId: customerId || null,
        customerData: customerInfo,
        subpageCount,
        selectedServices: mappedServices,
        selectedPackages: selectedPackages || [],
        discount,
        paymentTerms,
        customPaymentTerms,
        deliveryTime,
        validity,
        warranty,
        additionalInfo,
        quoteIntro,
        heroHeadline,
        heroSubline,
        nextSteps,
        customerArguments,
        includeDetailedDescriptions,
        quoteSections,
        videoUrl: videoUrl || null,
        passwordProtected: passwordProtected,
        viewPassword: passwordProtected && customPassword ? customPassword : null
      }

      // 4) Save or update
      let saved
      if (savedQuoteId) {
        // Update existing
        saved = await quotesApi.update(savedQuoteId, quoteData)
        saved = saved?.data || saved
        toast.success(`Angebot ${saved?.quoteNumber || ''} wurde aktualisiert!`)
      } else {
        // Create new
        const response = await quotesApi.create(quoteData)
        saved = response?.data || response
        toast.success(`Angebot ${saved?.quoteNumber || ''} wurde erstellt!`)
      }

      // 5) Store the quote ID and viewHash for future updates and link sharing
      if (saved?.id) {
        setSavedQuoteId(saved.id)
      }
      if (saved?.quoteNumber) {
        setSavedQuoteNumber(saved.quoteNumber)
      }
      if (saved?.viewHash) {
        setSavedViewHash(saved.viewHash)
      }
      
      // NO NAVIGATION - stay on current page!
    } catch (error) {
      console.error('Save quote error:', error)
      toast.error('Angebot konnte nicht gespeichert werden.')
    }
  }
  
  // Handle new quote from Top Nav
  const handleNewQuote = () => {
    // Reset for new quote
    setCustomerInfo({
      company: '',
      name: '',
      address: '',
      city: '',
      phone: '',
      email: ''
    })
    setSubpageCount(3)
    setSelectedServices({})
    setSelectedPackages([])
    setDiscount({
      oneTime: { type: 'percentage', value: 0, reason: '' }, 
      monthly: { type: 'percentage', value: 0, reason: '' }
    })
    setPaymentTerms('30-tage-netto')
    setCustomPaymentTerms('')
    setDeliveryTime('4-6 Wochen nach Auftragserteilung')
    setValidity('30 Tage gültig ab Erstellungsdatum')
    setWarranty('12 Monate Gewährleistung')
    setIncludeDetailedDescriptions(false)
    setSavedQuoteId(null)
    setSavedQuoteNumber(null)
    // When coaching is enabled, start at Kompass; otherwise at Firma
    setActiveTab(salesCoachingEnabled ? 'kompass' : 'firma')
    setQuoteSections([])
    toast.success('Neues Angebot gestartet')
  }

  // Handle unsaved changes dialog
  const handleConfirmLeave = () => {
    setShowUnsavedDialog(false)
    if (pendingNavigation) {
      if (typeof pendingNavigation === 'function') {
        pendingNavigation()
      } else {
        setActiveTab(pendingNavigation)
      }
      setPendingNavigation(null)
    }
  }

  const handleCancelLeave = () => {
    setShowUnsavedDialog(false)
    setPendingNavigation(null)
  }

  const handleSaveAndLeave = () => {
    // Call QuoteManager's save function if available
    if (quoteManagerRef.current && quoteManagerRef.current.handleSaveCurrentQuote) {
      quoteManagerRef.current.handleSaveCurrentQuote()
      // After saving, proceed with navigation
      setTimeout(() => {
        handleConfirmLeave()
      }, 500)
    } else {
      // Fallback: just proceed
      handleConfirmLeave()
    }
  }

  const contextValue = {
        // Navigation
        activeTab, setActiveTab,
        activeCompany, setActiveCompany,
        // Quote state
        selectedServices, setSelectedServices,
        selectedPackages, setSelectedPackages,
        subpageCount, setSubpageCount,
        discount, setDiscount,
        savedQuoteId, setSavedQuoteId,
        savedQuoteNumber, setSavedQuoteNumber,
        savedViewHash, setSavedViewHash,
        // Terms
        paymentTerms, setPaymentTerms,
        customPaymentTerms, setCustomPaymentTerms,
        savedCustomTerms, setSavedCustomTerms,
        deliveryTime, setDeliveryTime,
        validity, setValidity,
        warranty, setWarranty,
        additionalInfo, setAdditionalInfo,
        quoteIntro, setQuoteIntro,
        heroHeadline, setHeroHeadline,
        heroSubline, setHeroSubline,
        nextSteps, setNextSteps,
        // Services data
        services, packages, companies,
        categoryMap, categoryOrder,
        // Search
        searchQuery, setSearchQuery,
        searchFilters, setSearchFilters,
        collapsedCategories, setCollapsedCategories,
        expandedServices, setExpandedServices,
        // Custom position
        isCustomPositionOpen, setIsCustomPositionOpen,
        customPosition, setCustomPosition,
        // Collapsibles
        isRabatteOpen, setIsRabatteOpen,
        isZahlungsbedingungenOpen, setIsZahlungsbedingungenOpen,
        // Handlers
        handleAddCustomPosition,
        handlePackageToggle,
        calculatePricing,
        toggleCategory,
        getCategoryIcon,
        getCategoryName,
        canAccessTab,
        // Computed service data
        servicesByCategory,
        filteredServicesByCategory,
        // Quote sections
        quoteSections, setQuoteSections,
        // AI Intro
        isIntroDialogOpen, setIsIntroDialogOpen,
        isGeneratingIntro,
        introFormData, setIntroFormData,
        tempGeneratedIntro, setTempGeneratedIntro,
        tempAdditionalInfo, setTempAdditionalInfo,
        generateAIIntro, handleUseIntro,
        // Company/Customer
        customerInfo, setCustomerInfo,
        companyId,
        user,
      }

  return (
    <CalculatorProvider value={contextValue}>
    <div className="min-h-screen bg-slate-50">
      {/* New Top Navigation - STICKY */}
      <div className="sticky top-0 z-50">
        <TopNavigation
          activeCompany={activeCompany}
          onNavigate={handleTopNavigation}
          onSaveQuote={handleSaveQuoteFromNav}
          onNewQuote={handleNewQuote}
          onLogout={onLogout}
        />
      </div>

      {/* 4-Step Workflow Navigation — nur beim Angebots-Workflow */}
      {['kompass', 'firma', 'kunde', 'kalkulator', 'pdf', 'teilen'].includes(activeTab) && <WorkflowNavigator
        activeTab={activeTab}
        tabConfig={tabConfig}
        getTabStatus={getTabStatus}
        canAccessTab={canAccessTab}
        onTabChange={handleTabChangeWithWarning}
        onNewQuote={handleNewQuote}
        onSaveQuote={handleSaveQuoteFromNav}
        savedQuoteId={savedQuoteId}
        savedQuoteNumber={savedQuoteNumber}
      />}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className={`grid grid-cols-1 ${activeTab === 'kalkulator' ? 'lg:grid-cols-10' : 'lg:grid-cols-1'} gap-6`}>
          
          {/* Main Content Area - 70% width in Kalkulator (7 of 10 cols) */}
          <div className={activeTab === 'kalkulator' ? 'lg:col-span-7' : 'lg:col-span-1'}>
            
            {/* Dashboard */}
            {activeTab === 'dashboard' && (
              <Dashboard
                user={user}
                activeCompany={activeCompany}
                onNavigate={(type) => openManagement(type)}
                onNewQuote={handleNewQuote}
              />
            )}

            {/* Step 0: Sales-Kompass (nur wenn Coaching aktiviert) */}
            {activeTab === 'kompass' && salesCoachingEnabled && (
              <SalesCompass
                customerName={customerInfo?.name || customerInfo?.company}
                onComplete={(answers) => {
                  setSalesContext(answers);
                  localStorage.setItem('salesContext', JSON.stringify(answers));
                  setActiveTab('firma');
                }}
                onSkip={() => {
                  setSalesContext(null);
                  localStorage.removeItem('salesContext');
                  setActiveTab('firma');
                }}
              />
            )}

            {/* Step 1: Branding Selection */}
            {activeTab === 'firma' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-slate-800">
                    <BuildingOfficeIcon className="h-6 w-6 mr-2 text-slate-500" />
                    Schritt 1: Branding wählen
                    <div className="relative ml-2 group">
                      <svg className="h-5 w-5 text-slate-400 hover:text-slate-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div className="absolute left-0 top-full mt-2 w-72 p-3 bg-slate-800 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                        Kunden und Angebote sind <strong>firmenübergreifend</strong>. Hier wählen Sie nur das <strong>Erscheinungsbild</strong> (Logo, Farben, AGB) für dieses spezifische Angebot.
                      </div>
                    </div>
                  </CardTitle>
                  <p className="text-slate-600 mt-2">Wählen Sie das Branding (Logo, Farben, Firmendaten) für dieses Angebot.</p>
                  
                  {/* Weiter Button OBEN */}
                  {canAccessTab('kunde') && activeCompany && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-slate-700">
                          <CheckCircleIcon className="h-5 w-5 mr-2 text-emerald-500" />
                          <span className="font-medium">Branding ausgewählt: {activeCompany.name}</span>
                        </div>
                        <Button 
                          onClick={() => handleTabChangeWithWarning('kunde')}
                          className="bg-slate-900 hover:bg-slate-800 text-white"
                        >
                          Weiter
                          <ChevronRightIcon className="h-4 w-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <CompanyManager 
                    currentCompany={activeCompany}
                    onCompanySelect={setActiveCompany}
                  />
                  
                  {activeCompany && (
                    <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-slate-700">
                          <CheckCircleIcon className="h-5 w-5 mr-2 text-emerald-500" />
                          <span className="font-medium">Branding ausgewählt: {activeCompany.name}</span>
                        </div>
                        <Button 
                          onClick={() => handleTabChangeWithWarning('kunde')}
                          className="bg-slate-900 hover:bg-slate-800 text-white"
                        >
                          Weiter
                          <ChevronRightIcon className="h-4 w-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 2: Customer Selection */}
            {activeTab === 'kunde' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-slate-800">
                    <UserIcon className="h-6 w-6 mr-2 text-slate-500" />
                    Schritt 2: Kunde wählen
                  </CardTitle>
                  
                  {/* Weiter Button OBEN */}
                  {canAccessTab('kalkulator') && customerInfo.company && customerInfo.name && customerInfo.email && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-slate-700">
                          <CheckCircleIcon className="h-5 w-5 mr-2 text-emerald-500" />
                          <span className="font-medium">Kunde ausgewählt: {customerInfo.company}</span>
                        </div>
                        <Button 
                          onClick={() => handleTabChangeWithWarning('kalkulator')}
                          className="bg-slate-900 hover:bg-slate-800 text-white"
                        >
                          Weiter
                          <ChevronRightIcon className="h-4 w-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <CustomerInfo 
                    customerInfo={customerInfo}
                    setCustomerInfo={setCustomerInfo}
                    companyId={companyId}
                  />
                  
                  {customerInfo.company && customerInfo.name && customerInfo.email && (
                    <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-slate-700">
                          <CheckCircleIcon className="h-5 w-5 mr-2 text-emerald-500" />
                          <span className="font-medium">Kunde ausgewählt: {customerInfo.company}</span>
                        </div>
                        <Button 
                          onClick={() => handleTabChangeWithWarning('kalkulator')}
                          className="bg-slate-900 hover:bg-slate-800 text-white"
                        >
                          Weiter
                          <ChevronRightIcon className="h-4 w-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}


            {/* Step 3: Calculator */}
            <ServiceCalculatorCore />

            {/* Management Views */}
            {activeTab === 'account' && (
              <AccountSettings user={user} onUserUpdate={(updated) => {
                // Update local user state if parent exposes setter
              }} />
            )}

            {activeTab === 'apikeys' && (
              <ApiKeyManager user={user} />
            )}

            {activeTab === 'documents' && (
              <DocumentManager activeCompany={activeCompany} user={user} />
            )}
            
            {activeTab === 'management' && (
              <div className="space-y-6">
                {managementType === 'companies' && (
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">🏢 Firmen verwalten</h2>
                    <CompanyManager 
                      currentCompany={activeCompany}
                      onCompanySelect={setActiveCompany}
                    />
                  </div>
                )}
                {managementType === 'customers' && (
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">👥 Kunden verwalten</h2>
                    <CustomerManager 
                      companyId={companyId}
                      onClose={closeManagement}
                    />
                  </div>
                )}
                {managementType === 'quotes' && (
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">📄 Angebote verwalten</h2>
                    <QuoteManager 
                      ref={quoteManagerRef}
                      companyId={companyId}
                      currentSavedQuoteId={savedQuoteId}
                      savedQuoteId={savedQuoteId}
                      onQuoteSaved={setSavedQuoteId}
                      currentQuote={{
                        customerInfo: customerInfo,
                        selectedServices: selectedServices,
                        selectedPackages: selectedPackages,
                        subpageCount: subpageCount,
                        discount: discount,
                        quoteSections: quoteSections,
                        deliveryTime: deliveryTime,
                        validity: validity,
                        warranty: warranty,
                        paymentTerms: paymentTerms,
                        customPaymentTerms: customPaymentTerms,
                        includeDetailedDescriptions: includeDetailedDescriptions
                      }}
                      onLoadQuote={handleQuoteLoad}
                      onNewQuote={handleNewQuote}
                    />
                  </div>
                )}
                {managementType === 'services' && (
                  <ServiceManagement 
                    companyId={companyId}
                    onClose={closeManagement}
                    onServicesChange={loadData}
                  />
                )}
                {managementType === 'packages' && (
                  <PackageManager 
                    onClose={closeManagement}
                    onPackagesChange={loadData}
                  />
                )}
                {managementType === 'templates' && (
                  <TemplateManager 
                    onClose={closeManagement}
                    onTemplateSelect={(template) => {
                      // Add template to quote sections
                      const newSection = {
                        ...template,
                        id: Date.now().toString(),
                        position: quoteSections.length
                      }
                      setQuoteSections([...quoteSections, newSection])
                      closeManagement()
                      toast.success(`Vorlage "${template.title}" zum Angebot hinzugefügt`)
                    }}
                  />
                )}
                {managementType === 'email-templates' && (
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">✉️ E-Mail-Vorlagen verwalten</h2>
                    <EmailTemplateManager
                      companyId={activeCompany?.id}
                      companyInfo={activeCompany}
                      customerInfo={customerInfo}
                      quoteNumber={savedQuoteNumber || savedQuoteId}
                      viewUrl={savedViewHash ? `${window.location.origin}/quote/view/${savedViewHash}` : null}
                      calculatePricing={calculatePricing}
                      onSelectTemplate={setSelectedEmailTemplate}
                    />
                  </div>
                )}
                {managementType === 'revenue' && (
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">💵 Einnahmen verwalten</h2>
                    <RevenueManager 
                      onClose={closeManagement}
                      companyId={companyId}
                      companyName={activeCompany?.name}
                    />
                  </div>
                )}
                {managementType === 'expenses' && (
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">⚫ Ausgaben verwalten</h2>
                    <ExpenseManager 
                      onClose={closeManagement}
                      companyId={companyId}
                      companyName={activeCompany?.name}
                    />
                  </div>
                )}
                {managementType === 'cashflow' && (
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">💸 Cashflow-Übersicht</h2>
                    <CashflowDashboard 
                      companyId={companyId}
                    />
                  </div>
                )}
                {managementType === 'roadmap' && (
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">🚀 Roadmap</h2>
                    <Roadmap />
                  </div>
                )}
                {managementType === 'vertrieb' && (
                  <div>

                    <SalesToolsDashboard
                      quotes={allQuotes}
                      companyColors={companyColors}
                      company={activeCompany}
                    />
                  </div>
                )}
                {managementType === 'data-export' && (
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">📤 Export / Import</h2>
                    <DataExportImport 
                      companyId={companyId}
                      companyName={activeCompany?.name}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 4: PDF Preview */}
            {activeTab === 'pdf' && activeCompany && customerInfo.company && (
              <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-slate-800">
                    <DocumentIcon className="h-6 w-6 mr-2 text-slate-500" />
                    Schritt 4: PDF Vorschau
                  </CardTitle>
                  <p className="text-slate-600 mt-2">Überprüfen Sie Ihr Angebot und generieren Sie das PDF.</p>
                  
                  {/* Zurück Button OBEN */}
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-start">
                      <Button
                        onClick={() => handleTabChangeWithWarning('kalkulator')}
                        variant="outline"
                        className="border-slate-200 text-slate-600 hover:bg-slate-50"
                      >
                        <ChevronRightIcon className="h-4 w-4 mr-2 transform rotate-180" />
                        Zurück zu Schritt 3: Kalkulator
                      </Button>
                    </div>
                  </div>
                  
                  {/* Weiter Button */}
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-slate-700">
                        <CheckCircleIcon className="h-5 w-5 mr-2 text-emerald-500" />
                        <span className="font-medium">PDF bereit zum Teilen</span>
                      </div>
                      <Button 
                        onClick={() => handleTabChangeWithWarning('teilen')}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                      >
                        Weiter
                        <ChevronRightIcon className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <PdfPreview
                    companyInfo={activeCompany}
                    customerInfo={customerInfo}
                    selectedServices={Object.values(selectedServices)}
                    subpageCount={subpageCount}
                    pricing={{ total: calculatePricing.total, subtotal: calculatePricing.einmaligSubtotal + calculatePricing.laufendSubtotal }}
                    discount={discount}
                    savedQuoteId={savedQuoteId}
                    paymentTerms={paymentTerms}
                    customPaymentTerms={customPaymentTerms}
                    savedCustomTerms={savedCustomTerms}
                    quoteSections={quoteSections}
                    deliveryTime={deliveryTime}
                    validity={validity}
                    warranty={warranty}
                    additionalInfo={additionalInfo}
                    quoteIntro={quoteIntro}
                    nextSteps={nextSteps}
                    customerArguments={customerArguments}
                    includeDetailedDescriptions={includeDetailedDescriptions}
                    onIncludeDetailedDescriptionsChange={setIncludeDetailedDescriptions}
                  />
                </CardContent>
              </Card>
              
              {/* Video-Präsentation */}
              <VideoRecorderEnhanced
                onVideoReady={(base64Video) => setVideoUrl(base64Video)}
                existingVideoUrl={videoUrl}
              />
              </div>
            )}
            {/* Error State for PDF when data missing */}
            {activeTab === 'pdf' && (!activeCompany || !customerInfo.company) && (
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="text-slate-500">
                    <DocumentIcon className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>Bitte vervollständigen Sie zuerst die Schritte 1 und 2</p>
                    <div className="mt-4 space-x-3">
                      {!activeCompany && (
                        <Button onClick={() => handleTabChangeWithWarning('firma')} variant="outline">
                          Zurück zu Schritt 1
                        </Button>
                      )}
                      {!customerInfo.company && (
                        <Button onClick={() => handleTabChangeWithWarning('kunde')} variant="outline">
                          Zurück zu Schritt 2
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 5: Teilen */}
            {activeTab === 'teilen' && (
              <QuoteSharingStep
                savedQuoteId={savedQuoteId}
                savedQuoteNumber={savedQuoteNumber}
                savedViewHash={savedViewHash}
                passwordProtected={passwordProtected} setPasswordProtected={setPasswordProtected}
                customPassword={customPassword} setCustomPassword={setCustomPassword}
                selectedEmailTemplate={selectedEmailTemplate} setSelectedEmailTemplate={setSelectedEmailTemplate}
                activeCompany={activeCompany}
                customerInfo={customerInfo}
                companyId={companyId}
                onSave={handleSaveQuoteFromNav}
                onBack={() => handleTabChangeWithWarning('pdf')}
              />
            )}

                        {/* Error State for Teilen when data missing */}
            {activeTab === 'teilen' && (!activeCompany || !customerInfo.company) && (
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="text-slate-500">
                    <ChevronRightIcon className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>Bitte vervollständigen Sie zuerst die Schritte 1 und 2</p>
                    <div className="mt-4 space-x-3">
                      {!activeCompany && (
                        <Button onClick={() => handleTabChangeWithWarning('firma')} variant="outline">
                          Zurück zu Schritt 1
                        </Button>
                      )}
                      {!customerInfo.company && (
                        <Button onClick={() => handleTabChangeWithWarning('kunde')} variant="outline">
                          Zurück zu Schritt 2
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Pricing Overview */}
          {activeTab === 'kalkulator' && (
            <PricingSidebar
              activeCompany={activeCompany}
              customerInfo={customerInfo}
              selectedServices={selectedServices}
              calculatePricing={calculatePricing}
              companyColors={companyColors}
              discount={discount}
              canAccessTab={canAccessTab}
              onContinue={() => setActiveTab('pdf')}
            />
          )}
        </div>
      </div>
      
      {/* Sales-Coaching Widget - floating, only in Kalkulator and when coaching enabled */}
      {salesCoachingEnabled && activeTab === 'kalkulator' && (
        <CoachingWidget
          salesContext={salesContext}
          selectedServices={selectedServices}
          customerInfo={customerInfo}
          onReset={() => setActiveTab('kompass')}
        />
      )}

      {/* Alternative Positionen Dialog */}
      <ServiceAlternativesDialog
        open={alternativesDialogOpen}
        onClose={() => {
          setAlternativesDialogOpen(false)
          setCurrentAlternativeService(null)
        }}
        service={currentAlternativeService}
        onSave={handleSaveAlternatives}
      />

      {/* Unsaved Changes Dialog */}
      <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-amber-600">
              <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Nicht gespeicherte Änderungen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-600">
              Sie haben ein nicht gespeichertes Angebot. Möchten Sie es speichern bevor Sie fortfahren?
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleSaveAndLeave}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                💾 Speichern und fortfahren
              </Button>
              <Button
                onClick={handleConfirmLeave}
                variant="outline"
                className="border-amber-600 text-amber-600 hover:bg-amber-50"
              >
                Ohne speichern fortfahren
              </Button>
              <Button
                onClick={handleCancelLeave}
                variant="outline"
              >
                Abbrechen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
    </CalculatorProvider>
  )
}

export default Calculator